import React, { createContext, useContext, useState, useEffect } from 'react';
import { getSeasonalAccent } from '../utils/seasonalPalette';

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const getInitialTheme = () => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved;
    try {
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      return systemPrefersDark ? 'dark' : 'light';
    } catch (e) {
      return 'light';
    }
  };

  const [theme, setTheme] = useState(getInitialTheme);
  const [accent, setAccent] = useState(localStorage.getItem('accent') || 'forest');
  const [isSeasonal, setIsSeasonal] = useState(localStorage.getItem('isSeasonal') === 'true');
  const [musicProvider, setMusicProvider] = useState(localStorage.getItem('musicProvider') || 'youtube');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    // Only save to localStorage if the user explicitly clicked/changed it
    // Wait, keeping it saved so toggling overrides works.
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    try {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleSystemThemeChange = (e) => {
        if (!localStorage.getItem('theme')) {
          setTheme(e.matches ? 'dark' : 'light');
        }
      };
      mediaQuery.addEventListener('change', handleSystemThemeChange);
      return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
    } catch (e) {
      console.warn("prefers-color-scheme listener not supported in this browser environment.");
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('musicProvider', musicProvider);
  }, [musicProvider]);

  useEffect(() => {
    let activeAccent = accent;
    let customAccentColor = null;
    let customAccentSoftColor = null;
    
    if (isSeasonal) {
      const isDark = theme === 'dark';
      // Delegate to the shared seasonal palette module (src/utils/seasonalPalette.js) —
      // same date -> season -> accent-family -> monthly HSL shift used by the seasonal
      // tree tinting in TreeShelf.js, so the two stay in sync.
      const seasonal = getSeasonalAccent(new Date(), isDark);
      activeAccent = seasonal.accentFamily;
      customAccentColor = seasonal.color;
      customAccentSoftColor = seasonal.softColor;
    }

    document.documentElement.setAttribute('data-accent', activeAccent);
    
    // Apply custom monthly overrides or clean them up
    if (customAccentColor && customAccentSoftColor) {
      document.documentElement.style.setProperty('--accent', customAccentColor);
      document.documentElement.style.setProperty('--accent-soft', customAccentSoftColor);
    } else {
      document.documentElement.style.removeProperty('--accent');
      document.documentElement.style.removeProperty('--accent-soft');
    }

    localStorage.setItem('accent', accent);
    localStorage.setItem('isSeasonal', isSeasonal);
  }, [accent, isSeasonal, theme]);

  const toggleTheme = () => {
    setTheme(prev => {
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'lofi';
      if (prev === 'lofi') return 'sanctuary';
      return 'light';
    });
  };

  return (
    <ThemeContext.Provider value={{ 
      theme, setTheme, 
      accent, setAccent, 
      isSeasonal, setIsSeasonal,
      musicProvider, setMusicProvider,
      toggleTheme 
    }}>
      {children}
    </ThemeContext.Provider>
  );
};